
// @ts-nocheck
import type {NfServiceWorkerCommunication} from "@/services/NfServiceWorkerCommunication";
import {getAllFunctionsFromObject, getAllNonFunctionsFromObject, unPackProxyObject} from "@/utilities/nonDomUtils";
import { getRootElement} from "@/utilities/domUtils";
import {Subscription} from "rxjs";
import {loadTemplateRenderFunction} from "@/utilities/templateLoader";
import {getNavigationPageFromName, NavigationPageName, NavigationPages} from "@/frontendClasses/NavigationPages";
import {NavigateRequest} from "@/serviceWorker/workerComponents/MessageTypes/NavigateRequest";
import {createBlock, inject, openBlock, ref, resolveComponent, watch, WatchStopHandle} from "vue";
import {getNavigationPageRedirection} from "@/utilities/navigationUtils";
import {PageData} from "@/frontendClasses/PageData";
import {VNode, WatchCallback, WatchOptions, WatchSource} from "@vue/runtime-core";
import type {WritableComputedOptions} from "@vue/reactivity";
import {clone, isString, uniq} from "lodash-es";
import {Deferred} from "@/serviceWorker/workerComponents/Utilities/Deferred";
type ConstructorTypes = StringConstructor | NumberConstructor | BooleanConstructor | ArrayConstructor;
type InstanceOfConstructor<T> =  T extends StringConstructor ? string : (T extends NumberConstructor ? number: (T extends BooleanConstructor ? boolean: (T extends ArrayConstructor ? any[]: never)));
declare interface PropOptions<T extends ConstructorTypes> {
    type?: T;
    required?: boolean;
    default?: InstanceOfConstructor<T> | null | undefined;
    validator?(value: T): boolean;
}
export type NfHotelComponentInstance<T extends { }> = T & {
    [methodName in  keyof T['methods']]: T['methods'][methodName]
} & {
    [computedName in  keyof T['computed']]: ReturnType<T['computed'][computedName]>
};
var nextComponentUniqueId = 1;
/**
 * Klasa bazowa dla wszystkich komponentów
 */
export abstract class NfHotelComponentBase  {
    public abstract isMainComponent: boolean;
    protected communication: NfServiceWorkerCommunication|null = null;
    public abstract name: string;
    public abstract defaultTemplateName: string | null;
    public _templateName: string;
    /**
     * Czy początkowe dane z szablonu zostały załadowane
     */
    public initialDataLoaded: boolean = false;
    /**
     * Id komponentu unikalne w ramach jednego okna (iframe)
     */
    declare public uniqueId: number;
    /**
     * Tutaj są przechowywane subskrypcje, które muszą być usunięte po usunięciu komponentu
     */
    public subscriptions: Subscription[];
    abstract props: Record<string, unknown>;
    declare protected $: {
        type: this,
        isMounted: boolean,
        isUnmounted: boolean,
        isDeactivated: boolean,
        asyncResolved: boolean,
        render: Function
    };
    declare protected $options: this;

    /**
     * Zwraca funkcję, która będzie renderować podany komponent i mapować properki zgodnie z podaną mapą
     * @param componentName nazwa komponentu do wyrenderowania, hamburger case np. nf-hotel-room-standard
     * @param propertiesMap mapowanie properek komponentu - nazwa properki w komponencie (hamburger case) => kod zwracający  dane do tej properki w postaci stringa np. {"standard-data":"data.data.standardData"}
     */
    public static generateRenderFunctionForComponent(componentName: string, propertiesMap: Record<string,string>):Function {
        return function(_ctx) {
            const component = resolveComponent(componentName);
            const componentPropertiesMap = {};
            for(let key in propertiesMap) {
                componentPropertiesMap[key] =  (new Function('_ctx', 'with(_ctx) { return ' +propertiesMap[key] + ';}')(_ctx));
            }
            return (openBlock(), createBlock(component, componentPropertiesMap, null, 8, Object.keys(propertiesMap)));
        }
    }

    setRenderFunctionWhenLoaded(renderFunction: Function|null, renderFunctionPromise: Promise<Function|null>) {
        if(this.templateName) {
            renderFunction = null;
            renderFunctionPromise = loadTemplateRenderFunction(this.templateName);
            this.addWatch(() => this.$props.templateName, () => {
                this.setRenderFunctionWhenLoaded(renderFunction, renderFunctionPromise);
            })
        }
        if(renderFunctionPromise) {
            if (renderFunction) {
                //console.debug(`Render function for ${this.$options.name} preloaded`);
                if(this.$.render !== renderFunction) {
                    this.$.render = renderFunction;
                }
            } else {
                this.$.render = () => [];
                renderFunctionPromise.then(_renderFunction => {
                    if(this.$.render !== _renderFunction) {
                        this.$.render = _renderFunction;
                    }
                    if(!this.initialDataLoaded) {
                        this.loadInitialDataIfAvailable();
                    }
                    //console.debug(`Render function for ${this.$options.name} loaded`);
                    if(this.$.isMounted) {
                        this.forceUpdate();
                    }
                })
            }
        }
    }

    /**
     * Oznacza subskrypcję jako do usunięcia podczas wyjścia z komponentu
     * @param subscription
     */
    addLifeTimeSubscription(...subscriptions: Subscription[]) {
        for(let subscription of subscriptions)
        {
            if(!this.subscriptions) {
                this.subscriptions = [subscription];
            } else {
                this.subscriptions.push(subscription);
            }
        }
    }
    declare shadowRoot: DocumentFragment;
    /**
     * Zanim cokolwiek zostanie zainicjalizowane, zmienne $... jeszcze nie zainicjalizowane
     * Brak this
     */
    beforeCreate() {
        //console.debug(this.$options.name+' beforeCreate '+this.$.type.name, this,arguments, this.$el)
        //region ważne - kopiujemy metody z komponentu do this, ale bindujemy je do odpowiedniego this przy okazji
        for(let method of getAllFunctionsFromObject(this.$options)) {
            switch(method) {
                case 'created':
                case 'beforeMount':
                case 'mounted':
                case 'beforeCreate':
                case 'beforeUpdate':
                case 'updated':
                case 'data':
                case 'setRenderFunctionWhenLoaded':
                    continue;
            }
            if(method.startsWith('$')) {
                continue;
            }
            this[method] = this.$options[method].bind(this);
        }
        //endregion
        //region kopiujemy też properki z oryginalnego obiektu
        for(let property of getAllNonFunctionsFromObject(this.$options)) {
            switch(property) {
                case 'watch':
                case 'components':
                case 'methods':
                case 'props':
                case 'emits':
                case 'computed':
                case 'expose':
                case 'template':
                case 'render':
                case 'compilerOptions':
                    continue;
            }
            this[property] = clone(this.$options[property]);
        }
        //endregion

        //region wysyłamy info że szablon jest ładowany, żeby loader załadował style
        let parent = this.$parent;
        while(parent && (!parent.$el || !parent.$el.parentNode)) {
            parent = parent.$parent;
        }
        if(!parent || !parent.$el.parentNode) {
            if(inject('shadowRoot')) {
                parent = inject('shadowRoot');
            } else {
                return;
            }
        } else {
            parent = parent.$el;
        }
        this.shadowRoot = getRootElement(parent);
        let templateName = this.$props.templateName || this.$options.defaultTemplateName;
        this._templateName = templateName;
        let component = this;
        this.rootObject = null;
        this.stylesLoaded = ref(false);
        this.stylesLoadedDeferred = new Deferred<boolean>();
        const event = new CustomEvent('nfHotelElement.MountingTemplate', {
            detail: {
                templateName: templateName,
                setRootObject(rootObject) {
                    component.rootObject = rootObject;
                },
                setStylesLoadedPromise(promise) {
                    promise.then(() => {
                        component.stylesLoaded.value = true;
                        component.stylesLoadedDeferred.resolve(true);
                    })
                }
            },
            bubbles: true,
            composed: false
        });
        parent?.dispatchEvent(event);
        //endregion
    }
    loadInitialDataIfAvailable() {
        if(this.initialDataLoaded) {
            return;
        }
        const initialData = this.rootObject?.getTemplateInitialData(this._templateName);
        if(initialData) {
            for(let key in initialData) {
                this.$data[key] = this.initialData[key];
            }
            this.initialDataLoaded = true;
        }
    }


    /**
     * Zmienna $el nie zainicjalizowana
     * This dostępne
     */
    created() {
        this.uniqueId = nextComponentUniqueId++;
        this.loadInitialDataIfAvailable();
        //console.debug(this.$options.name+' created '+this.$.type.name, this,arguments, this.$el)
    }
    /**
     * Zmienna $el nie  zainicjalizowana
     */
    beforeMount() {
        //console.debug(this.$options.name+' beforeMount '+this.$.type.name, this,arguments, this.$el)

    }

    /**
     * Shadow root tego komponentu,
     * properka w instancji komponentu
     *
     */
    declare rootObject: NfBookingEngineElement|null;
    /**
     * Jest true, gdy globalne style CSS i specyficzne dla szablonu zostały załadowane
     */
    declare stylesLoaded: Ref<boolean>;
    declare stylesLoadedDeferred: Deferred<boolean>;

    canNavigate(pageOrName: NavigationPages | NavigationPageName): boolean {
        let navigationPage: NavigationPages;
        if (!isString(pageOrName)) {
            navigationPage = pageOrName;
        } else if (pageOrName in NavigationPages) {
            navigationPage = NavigationPages[pageOrName] as NavigationPages;
        } else {
            navigationPage = getNavigationPageFromName(pageOrName);
        }
        ({navigationPage} = getNavigationPageRedirection(navigationPage));
        switch (navigationPage) {
            case NavigationPages.shopPage:
                return !!+this.$data.config?.shop_items_available;
        }
        return true;
    }

    /**
     * Polecenie nawigacji do innej strony
     * @param navigationPage
     * @param navigationData
     * @returns {boolean} zwraca false, jeśli nawigacja została przechwycona przez zewnętrzny skrypt
     */
    navigate(navigationPage: NavigationPages|NavigationPageName, navigationData?: PageData) {
        if(!(navigationPage in NavigationPages) && !(isString(navigationPage) && getNavigationPageFromName(navigationPage) !== null)) {
            throw new Error('Nieprawidłowa strona nawigacji');
        }
        let _navigationPage : NavigationPages;
        if(isString(navigationPage)) {
            if(navigationPage in NavigationPages) {
                _navigationPage = NavigationPages[navigationPage] as NavigationPages;
            } else {
                _navigationPage = getNavigationPageFromName(navigationPage);
            }
        } else {
            _navigationPage = navigationPage;
        }
        ({ navigationPage, navigationData} = getNavigationPageRedirection(_navigationPage, navigationData));
        if(this.rootObject) {
            let navigationCaptured = false;
            if(this.rootObject.handleNavigationInComponents(navigationPage, navigationData)) {
                navigationCaptured = true;
            }
            if(this.rootObject.onNavigation(navigationPage, navigationData)) {
                navigationCaptured = true;
            }
            if(navigationCaptured) {
                return false;
            }
        }
        if(this.communication) {
            this.communication.sendMessage(new NavigateRequest(navigationPage, navigationData))
        }
        return true;
    }
    isAttachedToDocument(node: VNode) {
        if(node.el?.isConnected  && node.el.nodeType === Node.ELEMENT_NODE) {
            return true;
        }
        if(Array.isArray(node.children)) {
            for(let child of node.children) {
                if(this.isAttachedToDocument(child)) {
                    return true;
                }
            }
        }
        if(Array.isArray(node.dynamicChildren)) {
            for(let child of node.dynamicChildren) {
                if(this.isAttachedToDocument(child)) {
                    return true;
                }
            }
        }
    }
    getRootElements(node: VNode, result: HTMLElement[] = []) {
        if(node.el?.isConnected && node.el.nodeType === Node.ELEMENT_NODE) {
            result.push(node.el);
            return result;
        }
        if(node.children) {
            for(let child of node.children) {
                if (child.el?.isConnected && child.el?.nodeType === Node.ELEMENT_NODE) {
                    result.push(child.el);
                } else {
                    this.getRootElements(child, result);
                }

            }
        }
        if(node.dynamicChildren) {
            for(let child of node.dynamicChildren) {
                if (child.el?.isConnected && child.el?.nodeType === Node.ELEMENT_NODE) {
                    result.push(child.el);
                } else {
                    this.getRootElements(child, result);
                }

            }
        }
        return result;
    }
    /**
     * Zmienna $el  zainicjalizowana
     */
    mounted() {
        if(window.nfShowDebugMessages) {
            console.debug(this.$options.name+' mounted '+this.$.type.name, this,arguments, this.$el);
        }

        const intervalId = window.setInterval(() => {
            if(this.isAttachedToDocument(this.$.subTree)) {
                const rootElements = uniq(this.getRootElements(this.$.subTree));
                window.clearInterval(intervalId);
                let mountedEvent = new CustomEvent(this.name + '.mounted', {
                    detail: {
                        component: this,
                        elements: rootElements,
                        templateId: this.getTemplateId()
                    },
                    bubbles: true,
                    composed: false
                });
                this.$el?.dispatchEvent(mountedEvent);
                mountedEvent = new CustomEvent('NfHotelComponent.mounted', {
                    detail: {
                        component: this,
                        elements: rootElements,
                        templateId: this.getTemplateId()
                    },
                    bubbles: true,
                    composed: false
                });
                this.$el?.dispatchEvent(mountedEvent);
            }
        },5)
    }
    getTemplateId(): number {
        const templateName = this.$props.templateName || this.$options.defaultTemplateName;
        if (isFinite(templateName)) {
            return +templateName;
        }
        return window.nfHotelTemplates[templateName];
    }
    beforeUpdate() {
        //console.debug(this.$options.name+' beforeUpdate '+this.$.type.name, this,arguments);
    }
    updated() {
        //console.debug(this.$options.name+' updated '+this.$.type.name, this,arguments);
    }
    data(): object {
        return {
            templateVars: this.$props.templateVarsInit?unPackProxyObject(this.$props.templateVarsInit):{}
        };
    }
    declare computed: Record<any,WritableComputedOptions>;

    /**
     * Wymusza przerenderowanie widoku
     */
    declare $forceUpdate: () => void;
    updateScheduled = false;
    forceUpdate() {
        if(!this.updateScheduled && this.$.isMounted) {
            this.updateScheduled = true;
            window.setTimeout(() => {
                this.updateScheduled = false;
                this.$forceUpdate();
            });
        }
    }
    declare $emit: (eventName: string, ...eventData: any)=>void;
    beforeUnmount() {
        if(this.subscriptions) {
            for(let subscription of this.subscriptions) {
                subscription.unsubscribe();
            }
            this.subscriptions = [];
        }
        this.stopAllWatchers();
        if(this.communication) {
            this.communication.onRemoving();
        }
        this.$el?.dispatchEvent(new CustomEvent(this.name+'.unmounted',{detail: {component: this, templateId: this.getTemplateId()}, bubbles: true, composed: false}));
        this.$el?.dispatchEvent(new CustomEvent('NfHotelComponent.unmounted',{detail: {component: this, templateId: this.getTemplateId()}, bubbles: true, composed: false}));
    }

    /**
     * Dostęp do properek z atrybutów komponentu, używać tylko w podkomponentach, w komponencie głównym to jest w $mainProps
     */
    declare $props: { [K in keyof this['props']]:   Exclude<this['props'][K] extends ConstructorTypes ? InstanceOfConstructor<this['props'][K]> : ( this['props'][K] extends PropOptions<ConstructorTypes> ? this['props'][K]['type'] : this['props'][K] ), ConstructorTypes>  };
    declare $el: HTMLElement | null;
    /**
     * Dostęp do danych udostępnianych poprzez data do html, te dane są inicjalizowane w funkcji data()
     */
    declare $data: ReturnType<this['data']> ;



    private watchUnsubscribers: WatchStopHandle[] = [];

    /**
     * Zamiast używać funkcji Vue watch, użyj tej funkcji addWatch aby zarejestrować watchera który będzie automatycznie odrejestrowany gdy komponent będzie niszczony
     * @param source
     * @param cb
     * @param options
     */
    addWatch<T, Immediate extends Readonly<boolean> = false>(this:NfHotelComponentInstance<NfHotelPeopleConfiguration>, source: WatchSource<T>, cb: WatchCallback<T, Immediate extends true ? T | undefined : T>, options?: WatchOptions<Immediate>) {
        this.watchUnsubscribers.push(watch(source, cb, options));
    }

    /**
     * Funkcja automatycznie wywoływana podczas wymontowywania komponentu
     * Odrejestrowuje watchery dodane przez addWatch
     */
    stopAllWatchers() {
        for(let unsubscriber of this.watchUnsubscribers) {
            unsubscriber();
        }
        this.watchUnsubscribers = [];
    }
}
